import axios from 'axios';

// Base del backend. En dev queda vacío → '/api' relativo (lo redirige el proxy
// de CRA hacia http://localhost:5001). En producción se setea con
// REACT_APP_API_URL (ej. https://erpapi.snelapp.com), ya que el frontend
// (Cloudflare Pages) y el backend (droplet) viven en orígenes distintos.
export const API_BASE = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
});

// Inyectar token en cada request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('erp_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Contador: empresa activa seleccionada (el backend valida el vínculo aprobado).
  const acting = localStorage.getItem('erp_acting_tenant');
  if (acting) config.headers['x-acting-tenant'] = acting;
  return config;
});

// Si el token expira, redirigir a login
api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Solo el 401 (sesión inválida/expirada) cierra sesión. El 403 es
    // permiso/módulo denegado: la empresa no tiene ese módulo, pero la sesión
    // sigue siendo válida — NO sacar al usuario (si no, "entra y me saca").
    if (err.response?.status === 401) {
      localStorage.removeItem('erp_token');
      localStorage.removeItem('erp_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──
export const loginApi          = (data) => api.post('/auth/login', data);
export const validarTokenClave = (token) => api.get('/auth/definir-clave/validar', { params: { token } });
export const definirClaveApi   = (token, password) => api.post('/auth/definir-clave', { token, password });
export const registerTenantApi = (data) => api.post('/auth/register-tenant', data);
export const registerContadorApi = (data) => api.post('/auth/register-contador', data);
// Contador (multi-empresa)
export const contadorMisEmpresas = ()      => api.get('/contador/empresas');
export const contadorSolicitar   = (data)  => api.post('/contador/solicitar', data);
export const contadorMisReferidos = ()     => api.get('/contador/referidos');
// Empresa (admin aprueba a su contador)
export const contadorSolicitudes = ()      => api.get('/contador/solicitudes');
export const contadorPendientes  = ()      => api.get('/contador/solicitudes/pendientes');
export const contadorResponder   = (id, estado) => api.patch(`/contador/solicitudes/${id}`, { estado });
export const getMe             = ()     => api.get('/auth/me');

// ── Usuarios ──
export const getUsuarios       = ()        => api.get('/usuarios');
export const crearUsuario      = (data)    => api.post('/usuarios', data);
export const editarUsuario     = (id, d)   => api.put(`/usuarios/${id}`, d);
export const resetPasswordUsuario = (id, password) => api.put(`/usuarios/${id}/password`, { password });

// ── Perfiles (roles configurables) ──
export const getPerfiles       = ()        => api.get('/perfiles');
export const crearPerfil       = (data)    => api.post('/perfiles', data);
export const editarPerfil      = (id, d)   => api.put(`/perfiles/${id}`, d);
export const borrarPerfil      = (id)      => api.delete(`/perfiles/${id}`);

// ── Clientes ──
export const getClientes       = (params) => api.get('/clientes', { params });
export const getCliente        = (id)     => api.get(`/clientes/${id}`);
export const createCliente     = (data)   => api.post('/clientes', data);
export const updateCliente     = (id, d)  => api.put(`/clientes/${id}`, d);
export const deleteCliente     = (id)     => api.delete(`/clientes/${id}`);
export const getContactos      = (cid)    => api.get(`/clientes/${cid}/contactos`);
export const getCorreoRecepcion = (nit)   => api.get(`/correo-recepcion/${encodeURIComponent(nit)}`);
export const createContacto    = (cid, d) => api.post(`/clientes/${cid}/contactos`, d);
export const updateContacto    = (cid, id, d) => api.put(`/clientes/${cid}/contactos/${id}`, d);
export const deleteContacto    = (cid, id)    => api.delete(`/clientes/${cid}/contactos/${id}`);

// ── Cotizaciones ──
export const getCotizaciones    = (params) => api.get('/cotizaciones', { params });
export const getProximoCotizacion = ()       => api.get('/cotizaciones/proximo-numero');
export const setProximoCotizacion = (proximo) => api.put('/cotizaciones/proximo-numero', { proximo });
export const getCotizacion      = (id)     => api.get(`/cotizaciones/${id}`);
// Condiciones comerciales predefinidas
export const getCondiciones     = ()        => api.get('/condiciones');
export const crearCondicion     = (data)    => api.post('/condiciones', data);
export const actualizarCondicion= (id, data)=> api.put(`/condiciones/${id}`, data);
export const borrarCondicion    = (id)      => api.delete(`/condiciones/${id}`);
export const getAdjuntosCot     = (id)        => api.get(`/cotizaciones/${id}/adjuntos`);
export const subirAdjuntoCot    = (id, fd)    => api.post(`/cotizaciones/${id}/adjuntos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getAdjuntoCotBlob  = (id, adjId) => api.get(`/cotizaciones/${id}/adjuntos/${adjId}`, { responseType: 'blob' });
export const borrarAdjuntoCot   = (id, adjId) => api.delete(`/cotizaciones/${id}/adjuntos/${adjId}`);
// Adjuntos / soportes de facturas
export const getAdjuntosFac     = (id)        => api.get(`/facturacion/${id}/adjuntos`);
export const subirAdjuntoFac    = (id, fd)    => api.post(`/facturacion/${id}/adjuntos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getAdjuntoFacBlob  = (id, adjId) => api.get(`/facturacion/${id}/adjuntos/${adjId}`, { responseType: 'blob' });
export const borrarAdjuntoFac   = (id, adjId) => api.delete(`/facturacion/${id}/adjuntos/${adjId}`);
export const createCotizacion   = (data)   => api.post('/cotizaciones', data);
export const updateEstadoCot    = (id, e)  => api.patch(`/cotizaciones/${id}/estado`, { estado: e });
export const convertirAProyecto = (id, d)  => api.post(`/cotizaciones/${id}/convertir-proyecto`, d);
export const getEventosCot      = (id)     => api.get(`/cotizaciones/${id}/eventos`);
export const addEventoCot       = (id, d)  => api.post(`/cotizaciones/${id}/eventos`, d);

// ── Notificaciones ──
export const getNotificaciones     = ()    => api.get('/notificaciones');
export const marcarNotifLeida      = (id)  => api.patch(`/notificaciones/${id}/leer`);
export const marcarTodasLeidas     = ()    => api.patch('/notificaciones/leer-todas');

// ── Proyectos ──
export const getProyectos  = (params) => api.get('/proyectos', { params });
export const getProyecto   = (id)     => api.get(`/proyectos/${id}`);
export const createProyecto= (data)   => api.post('/proyectos', data);
export const updateProyecto= (id,data)=> api.put(`/proyectos/${id}`, data);
export const updateEstadoProy = (id, e) => api.patch(`/proyectos/${id}/estado`, { estado: e });
export const crearTarea    = (id, d)  => api.post(`/proyectos/${id}/tareas`, d);
export const updateTarea   = (tareaId, d) => api.put(`/proyectos/tareas/${tareaId}`, d);

// ── Órdenes de trabajo ──
export const getOrdenesTrabajo   = (params) => api.get('/ordenes-trabajo', { params });
export const getOrdenTrabajo     = (id)     => api.get(`/ordenes-trabajo/${id}`);
export const createOrdenTrabajo  = (data)   => api.post('/ordenes-trabajo', data);
export const updateOrdenTrabajo  = (id, d)  => api.put(`/ordenes-trabajo/${id}`, d);
export const updateEstadoOT      = (id, e)  => api.patch(`/ordenes-trabajo/${id}/estado`, { estado: e });

// ── Taller ──
export const getVehiculos        = (params) => api.get('/taller/vehiculos', { params });
export const getVehiculo         = (id)     => api.get(`/taller/vehiculos/${id}`);
export const createVehiculo      = (data)   => api.post('/taller/vehiculos', data);
export const updateVehiculo      = (id, d)  => api.put(`/taller/vehiculos/${id}`, d);
export const getOrdenesTaller    = (params) => api.get('/taller/ordenes', { params });
export const getOrdenTaller      = (id)     => api.get(`/taller/ordenes/${id}`);
export const createOrdenTaller   = (data)   => api.post('/taller/ordenes', data);
export const updateOrdenTaller   = (id, d)  => api.put(`/taller/ordenes/${id}`, d);
export const updateEstadoTaller  = (id, e)  => api.patch(`/taller/ordenes/${id}/estado`, { estado: e });
export const guardarItemsTaller  = (id, items) => api.put(`/taller/ordenes/${id}/items`, { items });
export const aprobarItemsTaller  = (id, d)  => api.patch(`/taller/ordenes/${id}/aprobar`, d);
export const consumirRepuestosTaller = (id) => api.post(`/taller/ordenes/${id}/consumir`);
export const enlazarFacturaTaller = (id, factura_id) => api.post(`/taller/ordenes/${id}/factura`, { factura_id });
export const getRecordatoriosTaller = (params) => api.get('/taller/recordatorios', { params });
export const createRecordatorioTaller = (data) => api.post('/taller/recordatorios', data);
export const updateRecordatorioTaller = (id, estado) => api.patch(`/taller/recordatorios/${id}`, { estado });

// ── Mantenimiento ──
export const getActivos          = (params) => api.get('/mantenimiento/activos', { params });
export const createActivo        = (data)   => api.post('/mantenimiento/activos', data);
export const updateActivo        = (id, d)  => api.put(`/mantenimiento/activos/${id}`, d);
export const getOrdenesMantenimiento = (p)  => api.get('/mantenimiento/ordenes', { params: p });
export const createOrdenMant     = (data)   => api.post('/mantenimiento/ordenes', data);
export const updateOrdenMant     = (id, d)  => api.put(`/mantenimiento/ordenes/${id}`, d);

// ── Compras ──
export const getProveedores          = (params) => api.get('/compras/proveedores', { params });
export const createProveedor         = (data)   => api.post('/compras/proveedores', data);
export const updateProveedor         = (id, d)  => api.put(`/compras/proveedores/${id}`, d);
export const getOrdenesCompra        = (params) => api.get('/compras/ordenes', { params });
export const getOrdenCompra          = (id)     => api.get(`/compras/ordenes/${id}`);
export const createOrdenCompra       = (data)   => api.post('/compras/ordenes', data);
export const updateOrdenCompra       = (id, d)  => api.put(`/compras/ordenes/${id}`, d);
export const emitirDocSoporte        = (id)     => api.post(`/compras/ordenes/${id}/documento-soporte`);
export const getDocSoportePDF        = (id)     => api.get(`/compras/ordenes/${id}/documento-soporte/pdf`, { responseType: 'blob' });
export const getOrdenCompraPDF       = (id)     => api.get(`/compras/ordenes/${id}/pdf`, { responseType: 'blob' });
export const enviarOrdenCompra       = (id, d)  => api.post(`/compras/ordenes/${id}/email`, d);
export const updateEstadoOC          = (id, e)  => api.patch(`/compras/ordenes/${id}/estado`, { estado: e });
export const getSolicitudesCompra    = (params) => api.get('/compras/solicitudes', { params });
export const createSolicitudCompra   = (data)   => api.post('/compras/solicitudes', data);
export const getSolicitudCompra      = (id)     => api.get(`/compras/solicitudes/${id}`);
export const updateSolicitudCompra   = (id, d)  => api.put(`/compras/solicitudes/${id}`, d);
export const updateEstadoSC          = (id, e)  => api.patch(`/compras/solicitudes/${id}/estado`, { estado: e });
export const convertirSCaOC          = (id, d)  => api.post(`/compras/solicitudes/${id}/convertir-oc`, d);
export const getSolicitudesPorCot    = (cotId)  => api.get('/compras/solicitudes', { params: { cotizacion_id: cotId } });
export const getSolicitudesPorProy   = (proyId) => api.get('/compras/solicitudes', { params: { proyecto_id: proyId } });

// ── Recepción de FE ──
export const getFacturasRecibidas = ()   => api.get('/recepcion');
export const getFacturaRecibida   = (id) => api.get(`/recepcion/${id}`);
export const uploadFacturaRecibida = (formData) => api.post('/recepcion/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getFacturaRecibidaPDF = (id)   => api.get(`/recepcion/${id}/pdf`, { responseType: 'blob' });
export const entradaBodegaPreview  = (id)    => api.get(`/recepcion/${id}/entrada-preview`);
export const generarEntradaBodega  = (id, d) => api.post(`/recepcion/${id}/entrada-inventario`, d);
export const setRecibidaItemsTipo  = (id, d) => api.patch(`/recepcion/${id}/items-tipo`, d);
export const setRecibidaItemsRetencion = (id, d) => api.patch(`/recepcion/${id}/items-retencion`, d);
export const autoClasificarRecibida    = (id) => api.post(`/recepcion/${id}/auto-clasificar`);
export const autoClasificarTodoRecibidas = () => api.post('/recepcion/auto-clasificar-todo');
export const setRecibidaNoDescontable = (id, valor) => api.patch(`/recepcion/${id}/no-descontable`, { valor });
export const getAsientoPreview    = (id)    => api.get(`/recepcion/${id}/asiento-preview`);
export const contabilizarRecibida = (id, d) => api.post(`/recepcion/${id}/contabilizar`, d);
export const descontabilizarRecibida = (id) => api.post(`/recepcion/${id}/descontabilizar`);
export const getRetencionesConfig = ()   => api.get('/recepcion/retenciones-config');
export const setRetencionesConfig = (d)  => api.put('/recepcion/retenciones-config', d);
// Certificados de retención (a proveedores)
export const getResumenRetenciones     = (params) => api.get('/retenciones/resumen', { params });
export const getCertificadoRetencionBlob = (params) => api.get('/retenciones/certificado', { params, responseType: 'blob' });
export const getRetencionesPracticadas  = (params) => api.get('/retenciones/practicadas', { params });
export const getRetencionesAFavor       = (params) => api.get('/retenciones/a-favor/resumen', { params });
export const getRetencionesAFavorDetalle = (params) => api.get('/retenciones/a-favor/detalle', { params });
export const getDesprendibleNominaPDF = (liqId) => api.get(`/nomina/liquidaciones/${liqId}/desprendible`, { responseType: 'blob' });
export const getBuzones    = ()      => api.get('/recepcion/buzones');
export const crearBuzon    = (d)     => api.post('/recepcion/buzones', d);
export const editarBuzon   = (id, d) => api.put(`/recepcion/buzones/${id}`, d);
export const borrarBuzon   = (id)    => api.delete(`/recepcion/buzones/${id}`);
export const probarBuzon   = (d)     => api.post('/recepcion/buzones/probar', d);
export const getEventosRecibida   = (fid)     => api.get(`/recepcion/${fid}/eventos`);
export const crearEventoRecibida  = (fid, d)  => api.post(`/recepcion/${fid}/eventos`, d);
export const enviarEventoRecibida = (id)      => api.post(`/recepcion/eventos/${id}/enviar`);
export const xmlEventoRecibidaUrl = (id)      => `${API_BASE}/api/recepcion/eventos/${id}/xml`;

// ── Facturación ──
export const getFacturas         = (params) => api.get('/facturacion', { params });
export const getFactura          = (id)     => api.get(`/facturacion/${id}`);
export const createFactura       = (data)   => api.post('/facturacion', data);
export const importarFacturas    = (formData) => api.post('/facturacion/importar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// ── Gastos / caja menor ──
export const getGastos           = ()         => api.get('/gastos');
export const crearGasto          = (formData) => api.post('/gastos', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const analizarGasto       = (formData) => api.post('/gastos/analizar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const confirmarGasto      = (id, data) => api.post(`/gastos/${id}/confirmar`, data);
export const borrarGasto         = (id)       => api.delete(`/gastos/${id}`);
export const gastoFotoUrl        = (id)       => `${API_BASE}/api/gastos/${id}/foto`;

// ── Recordatorio de estado de cuenta ──
export const enviarEstadoCuenta       = (clienteId, data) => api.post(`/email/estado-cuenta/${clienteId}`, data);
export const previewEstadoCuenta      = (clienteId, data) => api.post(`/email/estado-cuenta/${clienteId}/preview`, data);
export const getCorreosCliente        = (clienteId)       => api.get(`/email/correos-cliente/${clienteId}`);
export const enviarEstadoCuentaMasivo = (data)  => api.post('/email/estado-cuenta-masivo', data);
export const whatsappEstadoCuenta     = (clienteId)   => api.get(`/email/estado-cuenta/${clienteId}/whatsapp`);
export const whatsappEstadoCuentaMasivo = (soloVencidas) => api.get('/email/estado-cuenta-whatsapp-masivo', { params: { solo_vencidas: !!soloVencidas } });
export const getRecordatorioConfig    = ()      => api.get('/email/recordatorio-config');
export const setRecordatorioConfig    = (data)  => api.put('/email/recordatorio-config', data);
export const updateFactura       = (id, data) => api.put(`/facturacion/${id}`, data);
export const registrarPago       = (id, d)  => api.post(`/facturacion/${id}/pagos`, d);
export const updateEstadoFac     = (id, e)  => api.patch(`/facturacion/${id}/estado`, { estado: e });

// ── Reportes ──
export const getReportesDashboard   = (anio) => api.get('/reportes/dashboard', { params: anio ? { anio } : {} });
export const getIngresosMensuales   = ()    => api.get('/reportes/ingresos-mensuales');
export const getTopClientes         = ()    => api.get('/reportes/top-clientes');

// ── Contabilidad — Plan Único de Cuentas ──
export const getPUC       = (params)        => api.get('/contabilidad/puc', { params });
export const getCuentaPUC = (codigo)        => api.get(`/contabilidad/puc/${codigo}`);
export const crearCuenta  = (data)          => api.post('/contabilidad/puc', data);
export const editarCuenta = (codigo, data)  => api.patch(`/contabilidad/puc/${codigo}`, data);
export const borrarCuenta = (codigo)        => api.delete(`/contabilidad/puc/${codigo}`);

// ── Contabilidad — Configuración (cuentas por defecto) ──
export const getConfigContable = ()      => api.get('/contabilidad/config');
export const setConfigContable = (data)  => api.patch('/contabilidad/config', data);

// ── Contabilidad — Comprobantes ──
export const getComprobantesMeta = ()             => api.get('/contabilidad/comprobantes/meta');
export const getComprobantes     = (params)       => api.get('/contabilidad/comprobantes', { params });
export const getComprobante      = (id)           => api.get(`/contabilidad/comprobantes/${id}`);
export const crearComprobante    = (data)         => api.post('/contabilidad/comprobantes', data);
export const editarComprobante   = (id, data)     => api.patch(`/contabilidad/comprobantes/${id}`, data);
export const contabilizarComp    = (id)           => api.post(`/contabilidad/comprobantes/${id}/contabilizar`);
export const anularComprobante   = (id)           => api.post(`/contabilidad/comprobantes/${id}/anular`);
export const borrarComprobante   = (id)           => api.delete(`/contabilidad/comprobantes/${id}`);

// ── Contabilidad — Libros y reportes ──
export const getDiario           = (params) => api.get('/contabilidad/libros/diario', { params });
export const getMayor            = (params) => api.get('/contabilidad/libros/mayor', { params });
export const getBalance          = (params) => api.get('/contabilidad/reportes/balance', { params });
export const getEstadoResultados = (params) => api.get('/contabilidad/reportes/estado-resultados', { params });

// ── Contabilidad — Cierres de periodo ──
export const getCierres        = ()       => api.get('/contabilidad/cierres');
export const cerrarMensual     = (data)   => api.post('/contabilidad/cierres/mensual', data);
export const cerrarAnual       = (data)   => api.post('/contabilidad/cierres/anual', data);
export const reabrirCierre     = (id)     => api.delete(`/contabilidad/cierres/${id}`);

// ── Contabilidad — Información Exógena DIAN ──
export const getExogena1001 = (ano) => api.get('/contabilidad/exogena/1001', { params: { ano } });
export const getExogena1003 = (ano) => api.get('/contabilidad/exogena/1003', { params: { ano } });
export const getExogena1005 = (ano) => api.get('/contabilidad/exogena/1005', { params: { ano } });
export const getExogena1006 = (ano) => api.get('/contabilidad/exogena/1006', { params: { ano } });

// ── Nómina ──
export const getEmpleados      = (params)        => api.get('/nomina/empleados', { params });
export const getEmpleado       = (id)            => api.get(`/nomina/empleados/${id}`);
export const crearEmpleado     = (data)          => api.post('/nomina/empleados', data);
export const editarEmpleado    = (id, data)      => api.patch(`/nomina/empleados/${id}`, data);
export const borrarEmpleado    = (id)            => api.delete(`/nomina/empleados/${id}`);

// ── Recursos Humanos ──
export const rhContratos    = (empId)      => api.get(`/rh/empleados/${empId}/contratos`);
export const rhCrearContrato = (empId, d)  => api.post(`/rh/empleados/${empId}/contratos`, d);
export const rhEditarContrato = (id, d)    => api.put(`/rh/contratos/${id}`, d);
export const rhBorrarContrato = (id)       => api.delete(`/rh/contratos/${id}`);
export const rhDocumentos   = (empId)      => api.get(`/rh/empleados/${empId}/documentos`);
export const rhSubirDocumento = (empId, fd)=> api.post(`/rh/empleados/${empId}/documentos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const rhBorrarDocumento = (id)      => api.delete(`/rh/documentos/${id}`);
export const rhVacaciones   = (empId)      => api.get(`/rh/empleados/${empId}/vacaciones`);
export const rhSaldoVacaciones = (empId)   => api.get(`/rh/empleados/${empId}/vacaciones/saldo`);
export const rhCrearVacacion = (empId, d)  => api.post(`/rh/empleados/${empId}/vacaciones`, d);
export const rhResolverVacacion = (id, estado) => api.patch(`/rh/vacaciones/${id}`, { estado });
export const rhBorrarVacacion = (id)       => api.delete(`/rh/vacaciones/${id}`);
export const rhAusencias    = (empId)      => api.get(`/rh/empleados/${empId}/ausencias`);
export const rhCrearAusencia = (empId, fd) => api.post(`/rh/empleados/${empId}/ausencias`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const rhResolverAusencia = (id, estado) => api.patch(`/rh/ausencias/${id}`, { estado });
export const rhBorrarAusencia = (id)       => api.delete(`/rh/ausencias/${id}`);
export const rhEvaluaciones = (empId)      => api.get(`/rh/empleados/${empId}/evaluaciones`);
export const rhCrearEvaluacion = (empId, d)=> api.post(`/rh/empleados/${empId}/evaluaciones`, d);
export const rhBorrarEvaluacion = (id)     => api.delete(`/rh/evaluaciones/${id}`);
export const rhCertificadoUrl = (empId, salario) => `${API_BASE}/api/rh/empleados/${empId}/certificado?salario=${salario ? 'true' : 'false'}`;
export const getNominaConfig   = ()              => api.get('/nomina/config');
export const setNominaConfig   = (data)          => api.patch('/nomina/config', data);
export const getPeriodos       = (params)        => api.get('/nomina/periodos', { params });
export const getPeriodo        = (id)            => api.get(`/nomina/periodos/${id}`);
export const crearPeriodo      = (data)          => api.post('/nomina/periodos', data);
export const liquidarPeriodo   = (id)            => api.post(`/nomina/periodos/${id}/liquidar`);
export const cerrarPeriodo     = (id)            => api.post(`/nomina/periodos/${id}/cerrar`);
export const reabrirPeriodo    = (id)            => api.post(`/nomina/periodos/${id}/reabrir`);
export const borrarPeriodo     = (id)            => api.delete(`/nomina/periodos/${id}`);
export const ajustarLiq        = (id, data)      => api.patch(`/nomina/liquidaciones/${id}`, data);
export const getConceptosNomina = ()             => api.get('/nomina/conceptos');
export const getNovedades      = (liqId)         => api.get(`/nomina/liquidaciones/${liqId}/novedades`);
export const agregarNovedad    = (liqId, data)   => api.post(`/nomina/liquidaciones/${liqId}/novedades`, data);
export const eliminarNovedad   = (id)            => api.delete(`/nomina/novedades/${id}`);
export const enviarNominaDian  = (id)            => api.post(`/nomina/liquidaciones/${id}/enviar-dian`);
export const enviarNominaPeriodo = (id)          => api.post(`/nomina/periodos/${id}/enviar-dian`);

// ── SG-SST ──
export const getSgsstDashboard = (anio)   => api.get('/sgsst/dashboard', { params: { anio } });
export const getSgsstEmpleados = ()        => api.get('/sgsst/empleados');
export const getSgsstRiesgos   = ()        => api.get('/sgsst/riesgos');
export const crearSgsstRiesgo  = (d)       => api.post('/sgsst/riesgos', d);
export const editarSgsstRiesgo = (id, d)   => api.put(`/sgsst/riesgos/${id}`, d);
export const borrarSgsstRiesgo = (id)      => api.delete(`/sgsst/riesgos/${id}`);
export const getSgsstEventos   = ()        => api.get('/sgsst/eventos');
export const getSgsstEvento    = (id)      => api.get(`/sgsst/eventos/${id}`);
export const crearSgsstEvento  = (d)       => api.post('/sgsst/eventos', d);
export const editarSgsstEvento = (id, d)   => api.put(`/sgsst/eventos/${id}`, d);
export const borrarSgsstEvento = (id)      => api.delete(`/sgsst/eventos/${id}`);
export const getSgsstAcciones  = (params)  => api.get('/sgsst/acciones', { params });
export const crearSgsstAccion  = (d)       => api.post('/sgsst/acciones', d);
export const editarSgsstAccion = (id, d)   => api.put(`/sgsst/acciones/${id}`, d);
export const borrarSgsstAccion = (id)      => api.delete(`/sgsst/acciones/${id}`);
// Cluster B — factory para CRUD simples
const sgsstCrud = (rec) => ({
  list:   ()      => api.get(`/sgsst/${rec}`),
  get:    (id)    => api.get(`/sgsst/${rec}/${id}`),
  create: (d)     => api.post(`/sgsst/${rec}`, d),
  update: (id, d) => api.put(`/sgsst/${rec}/${id}`, d),
  remove: (id)    => api.delete(`/sgsst/${rec}/${id}`),
});
export const sgsstInspecciones   = sgsstCrud('inspecciones');
export const sgsstCondiciones    = sgsstCrud('condiciones');
export const sgsstCapacitaciones = sgsstCrud('capacitaciones');
export const sgsstEpp            = sgsstCrud('epp');
export const sgsstExamenes       = sgsstCrud('examenes');
export const sgsstAusentismo     = sgsstCrud('ausentismo');
// Cluster C
export const getSgsstEstandares  = ()        => api.get('/sgsst/estandares');
export const sgsstAutoeval       = sgsstCrud('autoevaluaciones');
export const sgsstPlan           = sgsstCrud('plan');
export const sgsstMatrizLegal    = sgsstCrud('matriz-legal');
export const sgsstPermisos       = sgsstCrud('permisos-trabajo');
export const sgsstBrigadistas    = sgsstCrud('brigadistas');
export const sgsstSimulacros     = sgsstCrud('simulacros');
export const sgsstPresupuesto    = sgsstCrud('presupuesto');

// ── Importar desde Siigo ──
export const siigoTest           = ()  => api.get('/siigo/test');
export const siigoPreviewClientes = () => api.get('/siigo/clientes/preview');
export const siigoImportClientes  = () => api.post('/siigo/clientes/import');
export const siigoDocsResumen    = ()            => api.get('/siigo/documentos/resumen');
export const siigoImportDocs     = (tipo, page)  => api.post('/siigo/documentos/import', { tipo, page });
export const siigoListDocs       = (tipo, page, cliente_nit) => api.get('/siigo/documentos', { params: { tipo, page, cliente_nit } });
export const getRemisiones       = (params)      => api.get('/remisiones', { params });

// ── Super Admin (plataforma) ──
export const superListTenants  = ()                 => api.get('/super/tenants');
export const superCrearTenant  = (data)             => api.post('/super/tenants', data);
export const superToggleTenant = (id, activo, motivo) => api.patch(`/super/tenants/${id}/estado`, { activo, motivo });
export const superSetPlan      = (id, plan)         => api.patch(`/super/tenants/${id}/plan`, { plan });
export const superSetModulos   = (id, data)         => api.patch(`/super/tenants/${id}/modulos`, data);
export const superRenovarSuscripcion = (id, data)   => api.patch(`/super/tenants/${id}/suscripcion`, data);
export const superGetTenantDian    = (id)       => api.get(`/super/tenants/${id}/dian`);
export const superSetTenantDian    = (id, data) => api.put(`/super/tenants/${id}/dian`, data);
export const superUploadTenantCert = (id, fd)   => api.post(`/super/tenants/${id}/dian/cert`, fd);
export const superEnviarFacturaPrueba = (id)     => api.post(`/super/tenants/${id}/dian/enviar-factura-prueba`);
export const superEnviarNotaPrueba    = (id, tipo) => api.post(`/super/tenants/${id}/dian/enviar-nota-prueba`, { tipo });
export const superEnviarSetCompleto   = (id)     => api.post(`/super/tenants/${id}/dian/enviar-set-completo`);
export const superEnviarDeposSet      = (id, body)  => api.post(`/super/tenants/${id}/dian/enviar-depos-set`, body || {});
export const superDeposEstado         = (id)        => api.get(`/super/tenants/${id}/dian/depos-estado`);
export const superEnviarNominaSet     = (id, cantidad) => api.post(`/super/tenants/${id}/dian/enviar-nomina-set`, { cantidad });
export const superSincronizarDs       = (id) => api.post(`/super/tenants/${id}/dian/sincronizar-ds`);
export const crearRemisionDesdeCotizacion = (cotId, data) => api.post(`/remisiones/desde-cotizacion/${cotId}`, data || {});
export const siigoDocPdfUrl      = (id)          => `/siigo/documentos/${id}/pdf`;
export const siigoGetDocPdfBlob  = (id)          => api.get(`/siigo/documentos/${id}/pdf`, { responseType: 'blob' });
export const siigoGenerarPdfsCotiz = ()          => api.post('/siigo/documentos/cotizaciones/pdf');
export const siigoPreviewProductos = ()          => api.get('/siigo/productos/preview');
export const siigoImportProductos  = ()          => api.post('/siigo/productos/import');

// ── Catálogo de productos ──
export const getRecurrentes   = ()         => api.get('/recurrentes');
export const getRecurrente    = (id)       => api.get(`/recurrentes/${id}`);
export const crearRecurrente  = (data)     => api.post('/recurrentes', data);
export const editarRecurrente = (id, data) => api.put(`/recurrentes/${id}`, data);
export const eliminarRecurrente = (id)     => api.delete(`/recurrentes/${id}`);
export const generarRecurrenteAhora = (id) => api.post(`/recurrentes/${id}/generar`);

export const getConfiguracion   = ()      => api.get('/configuracion');
export const updateConfiguracion = (data)  => api.put('/configuracion', data);

export const getImpuestos    = ()         => api.get('/parametros/impuestos');
export const crearImpuesto   = (data)     => api.post('/parametros/impuestos', data);
export const editarImpuesto  = (id, data) => api.put(`/parametros/impuestos/${id}`, data);
export const eliminarImpuesto = (id)      => api.delete(`/parametros/impuestos/${id}`);
export const getMonedas      = ()         => api.get('/parametros/monedas');
export const crearMoneda     = (data)     => api.post('/parametros/monedas', data);
export const editarMoneda    = (id, data) => api.put(`/parametros/monedas/${id}`, data);
export const eliminarMoneda  = (id)       => api.delete(`/parametros/monedas/${id}`);

export const getAprobReglas  = ()         => api.get('/aprobaciones/reglas');
export const crearAprobRegla = (data)     => api.post('/aprobaciones/reglas', data);
export const editarAprobRegla = (id, data) => api.put(`/aprobaciones/reglas/${id}`, data);
export const eliminarAprobRegla = (id)    => api.delete(`/aprobaciones/reglas/${id}`);
export const getAprobaciones = (estado)    => api.get('/aprobaciones', { params: { estado } });
export const resolverAprobacion = (id, data) => api.post(`/aprobaciones/${id}/resolver`, data);

export const getSucursales   = ()         => api.get('/sucursales');
export const crearSucursal   = (data)     => api.post('/sucursales', data);
export const editarSucursal  = (id, data) => api.put(`/sucursales/${id}`, data);
export const eliminarSucursal = (id)      => api.delete(`/sucursales/${id}`);

export const salidaBodegaPreview   = (id)    => api.get(`/remisiones/${id}/salida-preview`);
export const generarSalidaBodega   = (id, d) => api.post(`/remisiones/${id}/salida-inventario`, d);

export const ssoApi = (token) => api.post('/auth/sso', { token });

// Punto de Venta (POS)
export const posCaja       = ()  => api.get('/pos/caja');
export const posAbrirCaja  = (d) => api.post('/pos/caja/abrir', d);
export const posCerrarCaja = (d) => api.post('/pos/caja/cerrar', d);
export const posVenta      = (d) => api.post('/pos/venta', d);
export const posVentas     = (p) => api.get('/pos/ventas', { params: p });
export const posVentaDetalle = (id)    => api.get(`/pos/ventas/${id}`);
export const posDevolucion   = (id, d) => api.post(`/pos/ventas/${id}/devolucion`, d);
export const posCajas      = ()  => api.get('/pos/cajas');
export const posBuscarClientes = (q) => api.get('/pos/clientes', { params: { q } });
export const posCrearCliente   = (d) => api.post('/pos/clientes', d);
export const posFormas       = ()      => api.get('/pos/formas');
export const posFormasAdmin  = ()      => api.get('/pos/formas/admin');
export const posCrearForma   = (d)     => api.post('/pos/formas', d);
export const posEditarForma  = (id, d) => api.put(`/pos/formas/${id}`, d);
export const posBorrarForma  = (id)    => api.delete(`/pos/formas/${id}`);
export const posConfig     = ()  => api.get('/pos/config');
export const posVerificarPin = (pin) => api.post('/pos/verificar-pin', { pin });
export const posConfigSet  = (d) => api.put('/pos/config', d);
export const posCobroQR    = (d) => api.post('/pos/cobro-qr', d);
export const posCobroQREstado = (id) => api.get(`/pos/cobro-qr/${id}`);

export const getAlertasPrecio      = (estado) => api.get('/alertas-precio', { params: { estado } });
export const aplicarAlertaPrecio   = (id, d)  => api.post(`/alertas-precio/${id}/aplicar`, d);
export const descartarAlertaPrecio = (id)     => api.post(`/alertas-precio/${id}/descartar`);

export const getAlmacenes     = ()        => api.get('/inventario/almacenes');
export const crearAlmacen     = (d)       => api.post('/inventario/almacenes', d);
export const editarAlmacen    = (id, d)   => api.put(`/inventario/almacenes/${id}`, d);
export const eliminarAlmacen  = (id)      => api.delete(`/inventario/almacenes/${id}`);
export const getExistencias   = (params)  => api.get('/inventario/existencias', { params });
export const getKardex        = (params)  => api.get('/inventario/kardex', { params });
export const getMovimientosInv = ()       => api.get('/inventario/movimientos');
export const registrarMovInv  = (d)       => api.post('/inventario/movimientos', d);
export const registrarTraslado = (d)      => api.post('/inventario/traslados', d);
export const getConteos       = ()        => api.get('/inventario/conteos');
export const getConteo        = (id)      => api.get(`/inventario/conteos/${id}`);
export const crearConteo      = (d)       => api.post('/inventario/conteos', d);
export const guardarConteo    = (id, d)   => api.put(`/inventario/conteos/${id}`, d);
export const aplicarConteo    = (id)      => api.post(`/inventario/conteos/${id}/aplicar`);
export const eliminarConteo   = (id)      => api.delete(`/inventario/conteos/${id}`);
export const getLotes         = (params)  => api.get('/inventario/lotes', { params });
export const entradaLote      = (d)       => api.post('/inventario/lotes/entrada', d);
export const salidaLote       = (id, d)   => api.post(`/inventario/lotes/${id}/salida`, d);
export const getSeriales      = (params)  => api.get('/inventario/seriales', { params });
export const entradaSeriales  = (d)       => api.post('/inventario/seriales/entrada', d);
export const salidaSerial     = (id, d)   => api.post(`/inventario/seriales/${id}/salida`, d);

export const getIntegracionesPanel = ()   => api.get('/integraciones/panel');
export const getIntegracionesLogs  = (p)   => api.get('/integraciones/logs', { params: p });
export const getWebhooks       = ()        => api.get('/integraciones/webhooks');
export const crearWebhook      = (d)       => api.post('/integraciones/webhooks', d);
export const editarWebhook     = (id, d)   => api.put(`/integraciones/webhooks/${id}`, d);
export const eliminarWebhook   = (id)      => api.delete(`/integraciones/webhooks/${id}`);
export const probarWebhook     = (id)      => api.post(`/integraciones/webhooks/${id}/probar`);
export const getApiKeys        = ()        => api.get('/integraciones/api-keys');
export const crearApiKey       = (d)       => api.post('/integraciones/api-keys', d);
export const revocarApiKey     = (id)      => api.delete(`/integraciones/api-keys/${id}`);

export const getProductos   = (q)        => api.get('/productos', { params: { q } });
export const getProductoPorId = (id)     => api.get('/productos', { params: { id } });
export const crearProducto  = (data)     => api.post('/productos', data);
export const editarProducto = (id, data) => api.put(`/productos/${id}`, data);
export const borrarProducto = (id)       => api.delete(`/productos/${id}`);
export const autoClasificarProductos = () => api.post('/productos/auto-clasificar');

export const getLiquidacionImpuestos = (desde, hasta) => api.get('/impuestos/liquidacion', { params: { desde, hasta } });

// ── CMS Web ───────────────────────────────────────────────────────────────
export const getCmsConfig        = ()        => api.get('/cms/config').then(r=>r.data);
export const saveCmsConfig       = (d)       => api.put('/cms/config', d).then(r=>r.data);
export const getCmsBanners       = ()        => api.get('/cms/banners').then(r=>r.data);
export const createCmsBanner     = (d)       => api.post('/cms/banners', d).then(r=>r.data);
export const updateCmsBanner     = (id,d)    => api.put(`/cms/banners/${id}`, d).then(r=>r.data);
export const deleteCmsBanner     = (id)      => api.delete(`/cms/banners/${id}`);
export const getCmsServicios     = ()        => api.get('/cms/servicios').then(r=>r.data);
export const createCmsServicio   = (d)       => api.post('/cms/servicios', d).then(r=>r.data);
export const updateCmsServicio   = (id,d)    => api.put(`/cms/servicios/${id}`, d).then(r=>r.data);
export const deleteCmsServicio   = (id)      => api.delete(`/cms/servicios/${id}`);
export const getCmsProyectos     = ()        => api.get('/cms/proyectos').then(r=>r.data);
export const createCmsProyecto   = (d)       => api.post('/cms/proyectos', d).then(r=>r.data);
export const updateCmsProyecto   = (id,d)    => api.put(`/cms/proyectos/${id}`, d).then(r=>r.data);
export const deleteCmsProyecto   = (id)      => api.delete(`/cms/proyectos/${id}`);
export const getCmsBlog          = ()        => api.get('/cms/blog').then(r=>r.data);
export const createCmsBlogPost   = (d)       => api.post('/cms/blog', d).then(r=>r.data);
export const updateCmsBlogPost   = (id,d)    => api.put(`/cms/blog/${id}`, d).then(r=>r.data);
export const deleteCmsBlogPost   = (id)      => api.delete(`/cms/blog/${id}`);
export const getCmsEquipo        = ()        => api.get('/cms/equipo').then(r=>r.data);
export const createCmsEquipoMember = (d)     => api.post('/cms/equipo', d).then(r=>r.data);
export const updateCmsEquipoMember = (id,d)  => api.put(`/cms/equipo/${id}`, d).then(r=>r.data);
export const deleteCmsEquipoMember = (id)    => api.delete(`/cms/equipo/${id}`);
export const getCmsTestimonios   = ()        => api.get('/cms/testimonios').then(r=>r.data);
export const createCmsTestimonio = (d)       => api.post('/cms/testimonios', d).then(r=>r.data);
export const updateCmsTestimonio = (id,d)    => api.put(`/cms/testimonios/${id}`, d).then(r=>r.data);
export const deleteCmsTestimonio = (id)      => api.delete(`/cms/testimonios/${id}`);
export const getCmsProductos     = (params)  => api.get('/cms/productos', { params }).then(r=>r.data);
export const createCmsProducto   = (d)       => api.post('/cms/productos', d).then(r=>r.data);
export const updateCmsProducto   = (id,d)    => api.put(`/cms/productos/${id}`, d).then(r=>r.data);
export const deleteCmsProducto   = (id)      => api.delete(`/cms/productos/${id}`);
// Tienda web (venta directa con SnelPay)
export const subirFotoCms        = (archivo) => { const fd = new FormData(); fd.append('foto', archivo); return api.post('/cms/fotos', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r=>r.data); };
export const getTiendaConfig     = ()        => api.get('/tienda/config').then(r=>r.data);
export const saveTiendaConfig    = (d)       => api.put('/tienda/config', d).then(r=>r.data);
export const getTiendaCatalogo   = ()        => api.get('/tienda/catalogo').then(r=>r.data);
export const getTiendaEnvios     = ()        => api.get('/tienda/envios').then(r=>r.data);
export const createTiendaEnvio   = (d)       => api.post('/tienda/envios', d).then(r=>r.data);
export const updateTiendaEnvio   = (id,d)    => api.put(`/tienda/envios/${id}`, d).then(r=>r.data);
export const deleteTiendaEnvio   = (id)      => api.delete(`/tienda/envios/${id}`);
export const getPedidosWeb       = (params)  => api.get('/tienda/pedidos', { params }).then(r=>r.data);
export const accionPedidoWeb     = (id, accion, d={}) => api.post(`/tienda/pedidos/${id}/${accion}`, d).then(r=>r.data);

export default api;
